import axios from 'axios'

/**
 * How the backend says "this is about the plan, not about you".
 *
 *  - 402 `CAPACITY_EXCEEDED`: switching on a site or user would pass the plan's cap. Metadata
 *    carries the resource, the count in use, the limit and the plan code.
 *  - 403 `SUBSCRIPTION_PAST_DUE` / `SUBSCRIPTION_TRIAL_ENDED` / `SUBSCRIPTION_SUSPENDED` /
 *    `SUBSCRIPTION_ENDED`: the
 *    account's billing state refused the action. Metadata carries `status` and `planCode`. The
 *    trial-ended code is the same degraded state as past due, but with no balance to settle: the
 *    account never chose a plan. A plain permissions refusal is `FORBIDDEN` and must keep reading as one, because
 *    telling someone to pay when they merely lack a role is wrong.
 *
 * These are single problem objects (`{ message, errorCode, metadata }`), unlike the arrays that
 * 422 validation failures use.
 */

export type CapacityResource = 'sites' | 'users'

export interface CapacityRefusal {
  resource: CapacityResource
  current: number
  limit: number
  planCode: string
  /**
   * What the plan itself includes, when the refusal is about users. `limit` is this PLUS
   * `purchasedSeats`.
   *
   * Null means the API did not say — a refusal about sites, which cannot be bought, or an older
   * build. It is never defaulted to 0, because 0 is a real answer meaning "the plan includes none"
   * and the page says different things in the two cases.
   */
  includedSeats: number | null
  /** Users already bought on top of the allowance. Null for the same reasons; 0 is a real answer. */
  purchasedSeats: number | null
}

export type BillingRefusalKind = 'past_due' | 'trial_ended' | 'suspended' | 'ended'

export interface BillingRefusal {
  kind: BillingRefusalKind
  planCode: string | null
  /**
   * When the read-only wind-down window closes, on an `ended` refusal. ISO 8601 with offset, or null
   * on an account an operator cancelled by hand, which has no wind-down dates.
   */
  windDownEndsAt: string | null
  /**
   * Whether the account was on a free trial it never paid for. Sent by the API because the plan code
   * can no longer answer it: since the signup plan picker a trial runs inside `solo`, `growth` and the
   * rest. Absent from an older API build, in which case it reads as false and the wording falls back
   * to the one for an account that did subscribe.
   */
  trialOnly: boolean
}

export const PAST_DUE_MESSAGE =
  'This account is behind on payment, so changes are paused. Settle the balance to keep making changes. Guards can still clock in and report incidents.'
/**
 * A lapsed trial owes nothing, so it is never told to settle a balance. There is no self-serve picker
 * yet: the billing page is where a plan is chosen, by contacting support from there.
 */
export const TRIAL_ENDED_MESSAGE =
  'Your free trial has ended, so changes are paused. Choose a plan to keep making changes. Guards can still clock in and report incidents.'
export const INACTIVE_MESSAGE =
  'This account is inactive. Restart the subscription to use NezTrack again.'
/** A lapsed trial that was then suspended never subscribed, so there is nothing to restart. */
export const TRIAL_INACTIVE_MESSAGE =
  'Your free trial has ended and this account is inactive. Choose a plan to use NezTrack again.'

/**
 * A subscription that has ENDED, which is the opposite situation from a suspended one and needs the
 * opposite sentence.
 *
 * A suspended account is one we are still trying to get paid by, so it is told to restart or settle.
 * An ended account owes nothing and has left: what it needs to know is that it is read-only and how
 * long it has to take its data. Telling it to pay would be false, and paying would not help.
 */
export const ENDED_MESSAGE =
  'This subscription has ended, so the account is read-only. You can still view and export your data.'

/** The same, naming the date the data is deleted, when the API sent one. */
export function endedMessage(deletedOn: string | null): string {
  return deletedOn
    ? `This subscription has ended, so the account is read-only. You can view and export your data until it is deleted on ${deletedOn}.`
    : ENDED_MESSAGE
}

/**
 * Which "inactive" sentence fits. An account that was given a trial and never paid for anything has
 * no subscription to restart, so it is asked to choose a plan instead. Keyed on that fact and not on
 * the plan code, which since the signup plan picker says nothing about whether anything was paid.
 */
export function inactiveMessage(trialOnly: boolean): string {
  return trialOnly ? TRIAL_INACTIVE_MESSAGE : INACTIVE_MESSAGE
}

/**
 * What someone without `billing.view` is told instead. The account's payment standing is not theirs
 * to read, but "you don't have permission" would be false, so they get the fact and who can act on it.
 */
export const PAUSED_FOR_OTHERS_MESSAGE =
  'Changes are paused on this account. Ask your account administrator.'

export const BILLING_PATH = '/settings/billing'

type ProblemBody = { errorCode?: unknown; metadata?: unknown }

function problemBody(error: unknown): { status: number; body: ProblemBody } | null {
  if (!axios.isAxiosError(error) || !error.response) return null
  const data: unknown = error.response.data
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null
  return { status: error.response.status, body: data as ProblemBody }
}

function metadataOf(body: ProblemBody): Record<string, unknown> {
  return body.metadata && typeof body.metadata === 'object'
    ? (body.metadata as Record<string, unknown>)
    : {}
}

export function parseCapacityRefusal(error: unknown): CapacityRefusal | null {
  const problem = problemBody(error)
  if (!problem || problem.status !== 402 || problem.body.errorCode !== 'CAPACITY_EXCEEDED') return null

  const m = metadataOf(problem.body)
  const resource = m.resource === 'sites' || m.resource === 'users' ? m.resource : null
  if (!resource) return null
  return {
    resource,
    current: typeof m.current === 'number' ? m.current : 0,
    limit: typeof m.limit === 'number' ? m.limit : 0,
    planCode: typeof m.planCode === 'string' ? m.planCode : '',
    // Null rather than 0 when absent. The backend sends the pair or neither, so a missing one means
    // "not told", and a page that filled it with 0 would state a split it had never been given.
    includedSeats: typeof m.includedSeats === 'number' ? m.includedSeats : null,
    purchasedSeats: typeof m.purchasedSeats === 'number' ? m.purchasedSeats : null,
  }
}

export function parseBillingRefusal(error: unknown): BillingRefusal | null {
  const problem = problemBody(error)
  return problem ? billingRefusalFromBody(problem.status, problem.body) : null
}

/**
 * The same reading for a body that was decoded separately, such as a download's error, which axios
 * hands over as a Blob until the caller reads it as text.
 */
export function billingRefusalFromBody(status: number, data: unknown): BillingRefusal | null {
  if (status !== 403 || !data || typeof data !== 'object' || Array.isArray(data)) return null
  const body = data as ProblemBody

  const kind = billingRefusalKind(body.errorCode)
  if (!kind) return null

  const metadata = metadataOf(body)
  return {
    kind,
    planCode: typeof metadata.planCode === 'string' ? metadata.planCode : null,
    trialOnly: metadata.trialOnly === true,
    windDownEndsAt: typeof metadata.windDownEndsAt === 'string' ? metadata.windDownEndsAt : null,
  }
}

function billingRefusalKind(code: unknown): BillingRefusalKind | null {
  switch (code) {
    case 'SUBSCRIPTION_PAST_DUE':
      return 'past_due'
    case 'SUBSCRIPTION_TRIAL_ENDED':
      return 'trial_ended'
    case 'SUBSCRIPTION_SUSPENDED':
      return 'suspended'
    // Its own code since the account wind-down: a cancelled account used to answer with
    // SUBSCRIPTION_SUSPENDED and was therefore told to restart a subscription it had deliberately
    // ended, and to settle a balance it did not owe.
    case 'SUBSCRIPTION_ENDED':
      return 'ended'
    default:
      return null
  }
}

/**
 * Whether a Checkout refusal means the page's picture of the account is out of date.
 *
 * `CHECKOUT_UNAVAILABLE` is a 409 covering every precondition the endpoint checks before it calls
 * Stripe, and the one a customer can actually hit is "this account already has a subscription" — the
 * state moved on after the page loaded, usually because they subscribed in another tab. Refetching
 * is the remedy: the Subscribe card goes away and the portal card takes its place. The other causes
 * (no Terms version, an unprovisioned plan) are configuration, and refetching simply confirms that
 * the button should not have been there.
 *
 * Deliberately not folded into `isPlanRefusal`: that one means "the interceptor has already toasted
 * this", and Checkout's calls opt out of the toast so the card can speak for itself.
 */
export function isCheckoutUnavailable(error: unknown): boolean {
  const problem = problemBody(error)
  return problem?.status === 409 && problem.body.errorCode === 'CHECKOUT_UNAVAILABLE'
}

/**
 * Whether the API client already told the user about this failure: a plan refusal (402 capacity, 403
 * past-due/trial-ended/suspended) is toasted by the interceptor, so a call site that adds its own generic
 * "failed" toast on top would show two messages for one problem.
 */
export function isPlanRefusal(error: unknown): boolean {
  return parseCapacityRefusal(error) !== null || parseBillingRefusal(error) !== null
}

export function billingRefusalMessage(refusal: BillingRefusal, canViewBilling: boolean): string {
  if (!canViewBilling) return PAUSED_FOR_OTHERS_MESSAGE
  switch (refusal.kind) {
    case 'trial_ended':
      return TRIAL_ENDED_MESSAGE
    case 'past_due':
      return PAST_DUE_MESSAGE
    case 'suspended':
      return inactiveMessage(refusal.trialOnly)
    case 'ended':
      return ENDED_MESSAGE
  }
}

/**
 * "5 active sites" / "1 active user" — the cap is on what is switched on, not on what exists.
 *
 * Used for sites, and for a users refusal that arrived without the included/bought split. Both are
 * cases where all the sentence can honestly say is the total, so it says the total.
 */
function activeNoun(resource: CapacityResource, count: number): string {
  const noun = resource === 'sites' ? 'site' : 'user'
  return `${count} active ${noun}${count === 1 ? '' : 's'}`
}

export function capacityRefusalMessage(refusal: CapacityRefusal, canManageBilling: boolean): string {
  // Users can be bought; sites can only be upgraded to. Two different answers to "what do I do
  // now", and giving the wrong one sends a customer to a plan change they did not need.
  if (refusal.resource === 'users' && refusal.includedSeats !== null && refusal.purchasedSeats !== null) {
    const split =
      `Your plan includes ${refusal.includedSeats} user${refusal.includedSeats === 1 ? '' : 's'} ` +
      `and you have bought ${refusal.purchasedSeats}, so ${refusal.limit} ` +
      `${refusal.limit === 1 ? 'is' : 'are'} covered — and ${refusal.current} ` +
      `${refusal.current === 1 ? 'is' : 'are'} in use.`
    return canManageBilling
      ? `${split} Buy another to switch this one on.`
      : `${split} Ask your account administrator to buy another.`
  }

  const covered = `Your plan covers ${activeNoun(refusal.resource, refusal.limit)}, and ${refusal.current} ${
    refusal.current === 1 ? 'is' : 'are'
  } in use.`
  return canManageBilling
    ? `${covered} Upgrade your plan to switch on more.`
    : `${covered} Ask your account administrator to upgrade the plan.`
}

/**
 * The payload carries no upgrade link, so it is built here from the plan code. Today it lands on the
 * billing page, which says which limit was hit; the plan code rides along so a later checkout step can
 * start from the plan the account was on when it was refused.
 */
export function upgradePath(refusal: Pick<CapacityRefusal, 'resource' | 'planCode'>): string {
  const params = new URLSearchParams({ limit: refusal.resource })
  if (refusal.planCode) params.set('plan', refusal.planCode)
  return `${BILLING_PATH}?${params.toString()}`
}

/**
 * Sentence for a site that was created but is not switched on because the plan is full. The create
 * response does not carry the cap, so `maxSites` is whatever the billing query knows; without it
 * (the user cannot read billing) the sentence stays true without the number.
 */
export function parkedSiteMessage(maxSites: number | null | undefined): string {
  return typeof maxSites === 'number'
    ? `Site created, but it is not active, because your plan covers ${maxSites} ${
        maxSites === 1 ? 'site' : 'sites'
      }.`
    : "Site created, but it is not active, because it is beyond the number of sites your plan covers."
}
